import { ByteBuffer } from "flatbuffers";

import { Manifest } from "mannequin/src/types/tracking/mannequin";
import { PoserOptions } from "../poser/context";
import {
  ManifestMetadata,
  ManifestPlaysMap,
} from "mannequin/src/types/tracking/mannequin/manifest";
import { TrackLabels } from "mannequin/src/transform/track-consolidator/track-consolidator";
import { TrackingDataWire } from "mannequin/src/types/flatbuffers/mannequin";
import { TrackingDataWireLegacy } from "mannequin/src/types/tracking/mannequin/track/data";
import {
  VersionTag,
  versionStringHasLegacyJsonChunks,
} from "mannequin/src/types/tracking/mannequin/version-tag";
import { UniformMetadata } from "mannequin/src/types/tracking/mannequin/uniform";

function isMannequinSupported(_version: string): boolean {
  // placeholder function - for now just return true
  return true;
}

export class SkeletalApi {
  gamePk: number;
  private bearerToken?: string;
  private basePath?: string;
  private domain?: string;

  private mannequinVersion?: string;
  private legacyChunkFormat = false;

  private constructor(options: PoserOptions) {
    this.gamePk = options.gamePk;
    this.domain = options.trackingApiDomain;
    this.basePath = options.trackingApiBasePath;

    if (options.bearerToken) {
      this.setBearerToken(options.bearerToken);
    }
  }

  static async make(options: PoserOptions): Promise<SkeletalApi | undefined> {
    const api = new SkeletalApi(options);
    const versionTag = await api.fetchVersionsAvailable();

    if (versionTag) {
      const latest =
        options.mannequinVersionOverride &&
        versionTag.hasVersion(options.mannequinVersionOverride)
          ? options.mannequinVersionOverride
          : versionTag.latestVersion(false);
      if (!latest) {
        console.error("no mannequin data of any version found");
      } else if (isMannequinSupported(latest)) {
        console.log(`loading latest supported mannequin version: ${latest}`);
        api.mannequinVersion = latest;
        api.legacyChunkFormat = versionStringHasLegacyJsonChunks(latest);
      }
    } else {
      console.error("missing versions.json file, trying latest mannequin...");
      return undefined;
    }

    return api;
  }

  setBearerToken(bearerToken: string) {
    if (bearerToken != this.bearerToken) {
      this.bearerToken = bearerToken;
    }
  }

  fetchManifest(): Promise<Manifest> {
    return this.fetchJson(
      `${this.gamePk}/${this.mannequinVersion}/manifest.json`
    );
  }

  fetchMetadata(): Promise<ManifestMetadata> {
    return this.fetchJson(
      `${this.gamePk}/${this.mannequinVersion}/metadata.json`
    );
  }

  fetchTrackLabels(): Promise<TrackLabels> {
    return this.fetchJson(
      `${this.gamePk}/${this.mannequinVersion}/labels.json`
    );
  }

  fetchUniforms(): Promise<UniformMetadata> {
    return this.fetchJson(
      `${this.gamePk}/${this.mannequinVersion}/uniforms.json`
    );
  }

  private async fetchVersionsAvailable(): Promise<VersionTag | undefined> {
    return VersionTag.fromJSON(
      await this.fetchJson(`${this.gamePk}/versions.json`)
    );
  }

  async fetchChunk(
    num: number
  ): Promise<TrackingDataWire | TrackingDataWireLegacy> {
    if (this.legacyChunkFormat) {
      return this.fetchChunkJson(num);
    } else {
      return this.fetchChunkBinary(num);
    }
  }

  async fetchChunkBinary(num: number): Promise<TrackingDataWire> {
    const buffer = await this.fetchBuffer(
      `${this.gamePk}/${this.mannequinVersion}/${num}.bin`
    );
    const flatBuff = new ByteBuffer(new Uint8Array(buffer));
    return TrackingDataWire.getRootAsTrackingDataWire(flatBuff);
  }

  fetchChunkJson(num: number): Promise<TrackingDataWireLegacy> {
    return this.fetchJson(
      `${this.gamePk}/${this.mannequinVersion}/${num}.json`
    );
  }

  fetchPlays(): Promise<ManifestPlaysMap> {
    return this.fetchJson(`${this.gamePk}/${this.mannequinVersion}/plays.json`);
  }

  private fetchJson<T>(fileName: string): Promise<T> {
    if (this.basePath === undefined) {
      console.error(`skeletal api not fully initialized`);
      return Promise.reject("skeletal api not fully initialized");
    }
    // console.log(`fetching: ${this.domain}/${this.basePath}/${fileName}`);
    return fetch(
      `${this.domain}/${this.basePath}/${fileName}`,
      this.fetchOptions
    )
      .then((r) => {
        if (r.status !== 200) {
          return Promise.reject(
            `Failed to fetch game from Skeletal API. Status code: ${r.status}`
          );
        }
        return r.json();
      })
      .catch((e) => console.error(e));
  }

  private async fetchBuffer(fileName: string): Promise<ArrayBuffer> {
    if (this.basePath === undefined) {
      console.error(`skeletal api not fully initialized`);
      return Promise.reject("skeletal api not fully initialized");
    }
    const response = await fetch(
      `${this.domain}/${this.basePath}/${fileName}`,
      this.fetchOptions
    );

    if (response.status !== 200) {
      return Promise.reject(
        `Failed to fetch game from Skeletal API. Status code: ${response.status}`
      );
    }

    return response.arrayBuffer();
  }

  private get fetchOptions() {
    const headers = new Headers();
    if (this.bearerToken) {
      headers.set("Authorization", this.bearerToken);
    }
    return {
      method: "get",
      headers,
    };
  }
}
